import {
  CRAFT_TICK_INTERVAL,
  RAID_LOSS_CRAFT_DEBUFF_MULTIPLIER,
} from '@helpers/config';
import { getEntriesByType, getEntry } from '@helpers/content/content';
import { recipeResultQuantity } from '@helpers/crafting/recipe-result';
import { newEquipmentItem } from '@helpers/item/equipment';
import { rngSucceedsChance, rngUuid } from '@helpers/rng';
import { updateGamestate } from '@helpers/state-game';
import { townPickRecipeToQueue } from '@helpers/town/crafting/town-craft-pick';
import { resetTownSpecialtyPriority } from '@helpers/town/crafting/town-craft-priority-state';
import { townCraftQueueSize } from '@helpers/town/crafting/town-craft-queue-size';
import { townCraftTimeFor } from '@helpers/town/crafting/town-craft-time';
import { isTownCraftDebuffActive } from '@helpers/town/raid/town-raid-state';
import { townShopItemCap } from '@helpers/town/shop/town-shop-access';
import { applyTownStockAdd } from '@helpers/town/shop/town-stock';
import { applyTownMaterialDelta } from '@helpers/town/town-materials';
import {
  isTownDueForUpdate,
  markTownSubsystemProcessed,
} from '@helpers/town/town-tick';
import type {
  CraftQueueEntryId,
  GameState,
  RecipeContent,
  TownContent,
  TownCraftQueueEntry,
  TownId,
} from '@interfaces';

// Grants the result unconditionally (the caller already decided the craft succeeds) - a material result
// feeds the materials stash directly, only equipment lands in stock.
function grantCraftedResult(
  state: GameState,
  townId: TownId,
  recipe: RecipeContent,
): void {
  if ('itemId' in recipe.result) {
    applyTownMaterialDelta(
      state,
      townId,
      recipe.result.itemId,
      recipeResultQuantity(recipe),
    );
    return;
  }

  if ('equipmentId' in recipe.result) {
    const cap = townShopItemCap(townId);
    applyTownStockAdd(
      state,
      townId,
      { equipmentItem: newEquipmentItem(recipe.result.equipmentId) },
      cap,
    );
  }
}

function isStockFullFor(
  state: GameState,
  townId: TownId,
  recipe: RecipeContent,
): boolean {
  return (
    'equipmentId' in recipe.result &&
    state.world.towns[townId].stock.length >= townShopItemCap(townId)
  );
}

function completeCraft(
  state: GameState,
  townId: TownId,
  recipe: RecipeContent,
): void {
  grantCraftedResult(state, townId, recipe);
  resetTownSpecialtyPriority(state, townId, recipe.id);
}

// Returns false to signal "hold" - the entry stays in the queue untouched and retries next tick.
function resolveQueueEntryCompletion(
  state: GameState,
  town: TownContent,
  entry: TownCraftQueueEntry,
  recipe: RecipeContent,
): boolean {
  const target = state.world.towns[town.id];
  if (!target.tradeskills[entry.tradeskillId]) return true; // orphaned tradeskill - drop the entry defensively
  if (isStockFullFor(state, town.id, recipe)) return false;

  completeCraft(state, town.id, recipe);
  return true;
}

// Returns the entry to keep in the queue (advanced or held), or undefined once it's actually complete.
function advanceQueueEntry(
  state: GameState,
  town: TownContent,
  entry: TownCraftQueueEntry,
): TownCraftQueueEntry | undefined {
  const recipe = getEntry<RecipeContent>(entry.recipeId);
  if (!recipe) return undefined; // unresolvable - drop defensively

  const target = state.world.towns[town.id];
  const debuffMultiplier = isTownCraftDebuffActive(target)
    ? RAID_LOSS_CRAFT_DEBUFF_MULTIPLIER
    : 1;
  const level = target.tradeskills[entry.tradeskillId]?.level ?? 1;
  const craftTime = townCraftTimeFor(recipe, town, level, debuffMultiplier);
  const ticksIntoCraft = entry.ticksIntoCraft + 1;
  if (ticksIntoCraft < craftTime) return { ...entry, ticksIntoCraft };

  const completed = resolveQueueEntryCompletion(state, town, entry, recipe);
  return completed ? undefined : { ...entry, ticksIntoCraft };
}

// All queue entries advance/complete together in one pass - a town has multiple workers crafting in tandem, not one at a time.
function processExistingQueue(state: GameState, town: TownContent): void {
  const nextQueue: TownCraftQueueEntry[] = [];
  const target = state.world.towns[town.id];
  const currentQueue = target.craftQueue;

  currentQueue.forEach((entry) => {
    const kept = advanceQueueEntry(state, town, entry);
    if (kept) nextQueue.push(kept);
  });

  if (nextQueue.length === 0 && currentQueue.length === 0) return;

  target.craftQueue = nextQueue;
}

// Below craftingChanceItemThreshold (or the queue is empty), queue every chance it can, materials permitting;
// at/above it, only craftingChanceOnTick% per tick - gives players a window to see stock before it's replaced.
function shouldAttemptQueue(town: TownContent, queueLength: number): boolean {
  if (queueLength === 0) return true;
  if (queueLength < town.crafting.craftingChanceItemThreshold) return true;
  return rngSucceedsChance(town.crafting.craftingChanceOnTick);
}

function consumeRecipeRequirements(
  state: GameState,
  townId: TownId,
  recipe: RecipeContent,
): void {
  recipe.requirements.forEach((requirement) => {
    if ('itemId' in requirement) {
      applyTownMaterialDelta(
        state,
        townId,
        requirement.itemId,
        -requirement.quantity,
      );
    }
  });
}

// Returns whether a craft was actually queued.
function queueNewCraft(state: GameState, town: TownContent): boolean {
  const queueLength = state.world.towns[town.id].craftQueue.length;
  if (queueLength >= townCraftQueueSize(town)) return false;

  const pick = townPickRecipeToQueue(town);
  if (!pick) return false;

  consumeRecipeRequirements(state, town.id, pick.recipe);
  state.world.towns[town.id].craftQueue.push({
    id: rngUuid() as CraftQueueEntryId,
    tradeskillId: pick.tradeskillId,
    recipeId: pick.recipe.id,
    ticksIntoCraft: 0,
  });
  return true;
}

function maybeQueueNewCraft(state: GameState, town: TownContent): void {
  const queueLength = state.world.towns[town.id].craftQueue.length;
  if (!shouldAttemptQueue(town, queueLength)) return;

  queueNewCraft(state, town);
}

// Skips the per-tick chance roll; stops early once nothing more can be queued.
export function townQueueInitialCrafts(
  state: GameState,
  town: TownContent,
  count: number,
): void {
  for (let i = 0; i < count; i++) {
    if (!queueNewCraft(state, town)) return;
  }
}

// Instantly-finished equipment crafts, so a newly-found town's shop opens stocked.
export function townCompleteInitialCrafts(
  state: GameState,
  town: TownContent,
  count: number,
): void {
  for (let i = 0; i < count; i++) {
    const pick = townPickRecipeToQueue(
      town,
      (recipe) => 'equipmentId' in recipe.result,
    );
    if (!pick || isStockFullFor(state, town.id, pick.recipe)) return;

    consumeRecipeRequirements(state, town.id, pick.recipe);
    completeCraft(state, town.id, pick.recipe);
  }
}

function processTownCraftQueue(town: TownContent): void {
  updateGamestate((state) => {
    if (!state.world.towns[town.id]) return state;

    processExistingQueue(state, town);
    maybeQueueNewCraft(state, town);

    return state;
  });
}

export function townCraftProcessTick(): void {
  getEntriesByType<TownContent>('town').forEach((town) => {
    if (!isTownDueForUpdate(town.id, 'craft', CRAFT_TICK_INTERVAL)) return;

    processTownCraftQueue(town);
    markTownSubsystemProcessed(town.id, 'craft', CRAFT_TICK_INTERVAL);
  });
}
